import {HTTP, ERROR} from '@/http-common';

const state = {
  templates: [],
  history: [],
  loading: {
    templates: false,
    history: false,
    email: false,
    creatingTemplate: false,
    duplicatingTemplate: false,
    deletingTemplate: false,
    creatingEmail: false,
    updatingEmail: false,
    updatingTemplate: false,
    organizationSettings: false,
    referralEmails: false,
  },
  errors: {
    creatingTemplate: false,
    duplicatingTemplate: false,
    deletingTemplate: false,
    creatingEmail: false,
    fetchingEmail: false,
    fetchingEmailOptions: false,
    fetchingHistory: false,
    updatingEmail: false,
    updatingTemplate: false,
    organizationSettings: false,
    referralEmails: false,
  },
  newEmailOptions: {
    senders: []
  },
  emailMessage: {
    subject: '',
    header: '',
    body: '',
    sender: null,
    recipients_group: null,
    recipients_option: null,
    recipients: [],
    status: 0,
    id: null,
    template_id: null
  },
  template: {
    id: null,
    name: '',
    subject: '',
    header: '',
    sender: null,
    body: ''
  },
  organizationSettings:  {
    emailAddress: '',
    logo: "",
    organizationName: '',
    senderName: '',
    footerText: '',
  },
  lastCreatedTemplate: {},
  lastCreatedEmail: {},
  referralEmails: {
    emailBody: '',
    completedEmailBody: '',
  }
};

const mutations = {
  SET_LAST_CREATED_EMAIL_TEMPLATE(state, data) {
    state.lastCreatedTemplate = data
  },
  SET_EMAILS_TEMPLATES(state, data) {
    state.templates = data;
  },
  SET_EMAIL_HISTORY(state, data) {
    state.history = data;
  },
  SET_EMAIL_MESSAGE_OPTIONS(state, data) {
    state.newEmailOptions = data;
  },
  SET_EMAIL_TEMPLATE(state, data) {
    state.template = data;
  },
  SET_LAST_CREATED_EMAIL(state, data) {
    state.lastCreatedEmail = data;
  },
  SET_EMAIL_MESSAGE(state, data) {
    state.emailMessage = data;
  },
  SET_ERROR(state, data) {
    Object.assign(state.errors, data);
  },
  SET_LOADING(state, data) {
    Object.assign(state.loading, data);
  },
  SET_ORGANIZATION_SETTINGS(state, data) {
    Object.assign(state.organizationSettings, data);
  },
  SET_REFERRAL_EMAILS(state, {emailBody, completedEmailBody}) {
    Object.assign(state.referralEmails, {emailBody, completedEmailBody});
  },
};

const getters = {
  getTemplates: state => {
    return state.templates;
  },
  getTemplate: state => {
    return state.template;
  },
  getLastCreatedTemplate: state => {
    return state.lastCreatedTemplate
  },
  getErrors: state => {
    return state.errors;
  },
  getLoading: state => {
    return state.loading;
  },
  getHistory: state => {
    return state.history;
  },
  getEmail: state => {
    return state.emailMessage;
  },
  getNewEmailOptions: state => {
    return state.newEmailOptions
  },
  getOrganizationSettings: state => {
    return state.organizationSettings
  },
  getReferralEmails: state => {
    return state.referralEmails
  },
};

/***
 * Wrapper around request that sets loading and errors flags
 * @param request function that creates a request and returns a promise
 * @param commit vuex commit function
 * @param errorKey key for SET_ERROR
 * @param loadingKey key for SET_LOADING
 */
function handleRequest(request, commit, errorKey, loadingKey){
  commit('SET_LOADING', {loadingKey: true});
  return request().then(response => {
    ERROR(response);
    if(response.data.status === 'success') {
      commit('SET_ERROR', {errorKey: false});
    } else {
      commit('SET_ERROR', {errorKey: true});
      throw Error('Request error');
    }
    commit('SET_LOADING', {loadingKey: false});
    return response;
  }).catch(err => {
    commit('SET_LOADING', {loadingKey: false});
    commit('SET_ERROR', {errorKey: true});
    throw err;
  })
}

const actions = {
  fetchNewEmailOptions: ({commit}) => {
    return handleRequest(
      () => HTTP.post('emails/new-email-options', true),
      commit,
      'fetchingOptions',
      'email'
    ).then(response => {
      commit('SET_EMAIL_MESSAGE_OPTIONS', response.data.data);
    })
  },
  fetchEmail: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.get('emails/get/' + payload),
      commit,
      'fetchingEmail',
      'email'
    ).then(response => {
      commit('SET_EMAIL_MESSAGE', response.data.data.email_message);
    })
  },
  fetchHistory: ({commit}) => {
    return handleRequest(
      () => HTTP.post(`email-history/index`),
      commit,
      'fetchingHistory',
      'history'
    ).then(response => {
      commit('SET_EMAIL_HISTORY', response.data.data.history);
    })
  },
  fetchTemplate: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.get('emails-templates/get/' + payload),
      commit,
      'fetchingTemplate',
      'template'
    ).then(response => {
      commit('SET_EMAIL_TEMPLATE', response.data.data.template);
    })
  },
  fetchTemplates: ({commit}) => {
    return handleRequest(
      () => HTTP.post(`emails-templates/index`),
      commit,
      'fetchingTemplates',
      'templates'
    ).then(response => {
      commit('SET_EMAILS_TEMPLATES', response.data.data.templates);
    })
  },
  createTemplate: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.post('emails-templates/create', payload),
      commit,
      'creatingTemplate',
      'creatingTemplate'
    ).then(response => {
      commit('SET_LAST_CREATED_EMAIL_TEMPLATE', response.data.data.template);
      commit('SET_EMAIL_TEMPLATE', response.data.data.template);
    })
  },
  updateTemplate: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.post('emails-templates/update', payload),
      commit,
      'updatingTemplate',
      'updatingTemplate'
    ).then(response => {
      commit('SET_EMAIL_TEMPLATE', response.data.data.template);
      commit('SET_LAST_CREATED_EMAIL_TEMPLATE', response.data.data.template);
    })
  },
  duplicateTemplate: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.post('emails-templates/duplicate', payload),
      commit,
      'duplicatingTemplate',
      'duplicatingTemplate'
    ).then(response => {
      commit('SET_LAST_CREATED_EMAIL_TEMPLATE', response.data.data.template);
    })
  },
  deleteTemplate: ({commit, dispatch}, payload) => {
    return handleRequest(
      () => HTTP.post('emails-templates/delete', payload),
      commit,
      'deletingTemplate',
      'deletingTemplate'
    ).then(response => {
      dispatch('resetTemplate');
    })
  },
  createEmail: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.post('emails/create', {...payload, email: state.emailMessage}),
      commit,
      'creatingEmail',
      'creatingEmail'
    ).then(response => {
      commit('SET_EMAIL_MESSAGE', response.data.data.email_message);
    })
  },
  updateEmail: ({commit}, payload) => {
    return handleRequest(
      () => HTTP.post('emails/update', {...payload, email: state.emailMessage}),
      commit,
      'updatingEmail',
      'updatingEmail'
    ).then(response => {
      commit('SET_EMAIL_MESSAGE', response.data.data.email_message);
    })
  },
  fetchOrganizationSettings: ({commit}, {organizationId}) => {
    return handleRequest(
      () => HTTP.get(`/accounts/${organizationId}/email_settings/`),
      commit,
      'organizationSettings',
      'organizationSettings'
    ).then(response => {
      commit('SET_ORGANIZATION_SETTINGS', response.data.data);
    })
  },
  updateOrganizationSettings: ({commit}, {organizationId, ...payload}) => {
    return handleRequest(
      () => HTTP.post(`/accounts/${organizationId}/email_settings/`, payload),
      commit,
      'organizationSettings',
      'organizationSettings'
    ).then(response => {
      commit('SET_ORGANIZATION_SETTINGS', response.data.data);
    })
  },
  fetchReferralEmails: ({commit}, {formId}) => {
    return handleRequest(
      () => HTTP.get(`/referral/${formId}/body/`),
      commit,
      'referralEmails',
      'referralEmails'
    ).then(response => {
      commit('SET_REFERRAL_EMAILS', response.data.data);
    })
  },
  updateReferralEmails: ({commit}, {formId, ...data}) => {
    return handleRequest(
      () => HTTP.post(`/referral/${formId}/body/`, {...data}),
      commit,
      'referralEmails',
      'referralEmails'
    ).then(response => {
      commit('SET_REFERRAL_EMAILS', response.data.data);
    })
  },
  resetEmail({commit}) {
    commit('SET_EMAIL_MESSAGE', {
      subject: '',
      header: '',
      body: '',
      sender: null,
      recipients_group: null,
      recipients_option: null,
      recipients: [],
      status: 0,
      id: null,
      template_id: null
    })
  },
  resetTemplate({commit}) {
    commit('SET_EMAIL_TEMPLATE', {
      id: null,
      name: '',
      subject: '',
      header: '',
      sender: null,
      body: ''
    });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};



// WEBPACK FOOTER //
// ./src/store/modules/emails.js